import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import { Node as PMNode, type Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { Suggestion } from '../../aiSuggestionsPluginType';

import { wrapNode } from './toolCalls/wrapNode';

function createNodeFromToolCallValue(schema: Schema, value: string): PMNode | undefined {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value;
		const node = processRawValue(schema, parsed);
		return node;
	} catch {
		return undefined;
	}
}

function getPositionsByLocalId(
	doc: PMNode,
	localId: string,
): { from: number; to: number } | undefined {
	let result: { from: number; to: number } | undefined;
	doc.descendants((node, pos) => {
		if (result) {
			return false;
		}
		if (node.attrs.localId === localId) {
			result = { from: pos, to: pos + node.nodeSize };
		}
		return !result;
	});
	return result;
}

export const getInversion = (tr: Transaction, suggestion: Suggestion): Transform => {
	const localIdToPos = new Map<string, { from: number; to: number }>();
	tr.doc.descendants((node, pos) => {
		const localId = node.attrs?.localId;
		if (localId) {
			localIdToPos.set(localId, { from: pos, to: pos + node.nodeSize });
		}
		return true;
	});
	/**
	 * For some reason, without transforming the doc back and forth,
	 * the steps are missing in subsequent transforms.
	 */
	let transform = new Transform(PMNode.fromJSON(tr.doc.type.schema, tr.doc.toJSON()));
	/**
	 * We reverse the toolCall order so if there are multiple
	 * tool calls targeting the same localId,
	 * they will be applied in order
	 */
	suggestion.toolCalls.reverse().forEach((toolCall) => {
		const localId = 'localId' in toolCall.arguments ? toolCall.arguments.localId : undefined;
		if (localId === undefined) {
			return;
		}

		const localIdPositions = getPositionsByLocalId(transform.doc, localId);
		if (!localIdPositions) {
			return;
		}
		if ('value' in toolCall.arguments) {
			const value = toolCall.arguments.value;
			const node = createNodeFromToolCallValue(tr.doc.type.schema, value);
			if (!node) {
				return;
			}

			switch (toolCall.name) {
				case 'replaceNode':
					transform = transform.replaceWith(localIdPositions.from, localIdPositions.to, node);
					break;
				case 'insertNodeBefore':
					transform = transform.replaceWith(localIdPositions.from, localIdPositions.from, node);
					break;
				case 'insertNodeAfter':
					transform = transform.replaceWith(localIdPositions.to, localIdPositions.to, node);
					break;
			}
		} else {
			switch (toolCall.name) {
				case 'removeNode':
					transform = transform.delete(localIdPositions.from, localIdPositions.to);
					break;
				case 'wrap':
				case 'wrapNode':
					wrapNode({
						toolCallArguments: toolCall.arguments,
						tr: transform,
						pos: localIdPositions.from,
					});

					break;
				case 'setAttrs':
					const node = transform.doc.nodeAt(localIdPositions.from);
					if (!node) {
						break;
					}
					const attrs = {
						...node.attrs,
						...toolCall.arguments.attrs,
					};
					transform = transform.setNodeMarkup(localIdPositions.from, undefined, attrs);
					break;
				default:
					break;
			}
		}
	});
	return transform;
};
